import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { marked } from 'marked';
import { AssistantCitation, AssistantService, AssistantToolCall } from '../assistant.service';

interface AssistantThreadMessage {
    role: 'user' | 'assistant';
    content: string;
    // Rendered markdown -> HTML for assistant answers only. Bound via
    // [innerHTML], which Angular sanitises (scripts/handlers stripped), so
    // model output can never inject active content. User/error bubbles stay
    // as plain-text {{ content }} and never get this.
    html?: string;
    error?: boolean;
    citations?: AssistantCitation[];
    toolCalls?: AssistantToolCall[];
}

// Cap the conversation context sent to the backend.
export const MAX_HISTORY_TURNS = 6;

@Component({
    selector: 'app-support-assistant',
    templateUrl: './support-assistant.component.html',
    styleUrls: ['./support-assistant.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SupportAssistantComponent {
    @ViewChild('thread') thread?: ElementRef<HTMLElement>;

    messages: AssistantThreadMessage[] = [];
    question = '';
    sending = false;

    // Starter prompts across the dashboard's data domains the tools cover.
    readonly suggestions = [
        'Give me the headlines for the board.',
        'How many incidents are open, and how many are overdue?',
        'How are we tracking against budget this financial year?',
        'How many staff have expired or missing compliance items?',
    ];

    // Last question that failed — retried without re-adding a user bubble. Null when retrying can't help (503, 429).
    private lastQuestion: string | null = null;

    constructor(private assistantService: AssistantService, private cdr: ChangeDetectorRef) {}

    get isValid(): boolean {
        return this.question.trim().length > 0;
    }

    get canRetry(): boolean {
        return !this.sending && this.lastQuestion !== null;
    }

    onEnter(event: Event): void {
        const e = event as KeyboardEvent;
        if (e.shiftKey) return; // Shift+Enter inserts a newline
        e.preventDefault();
        this.send();
    }

    send(): void {
        if (!this.isValid || this.sending) return;

        const question = this.question.trim();
        this.question = '';
        this.messages.push({ role: 'user', content: question });
        this.request(question);
    }

    askSuggestion(question: string): void {
        if (this.sending) return;

        this.messages.push({ role: 'user', content: question });
        this.request(question);
    }

    // The assistant's tools return aggregate KPIs, not row-level entity references, so the
    // backend emits no citations and the chips never render. Kept as a no-op so
    // the template binding stays valid if citations are added later.
    openCitation(_citation: AssistantCitation): void {
        // intentionally empty
    }

    toolTrace(message: AssistantThreadMessage): string {
        const calls = message.toolCalls ?? [];
        const trace = `Checked ${calls.length} data source${calls.length === 1 ? '' : 's'}`;
        return calls.some(c => c.error) ? `${trace} — some queries failed` : trace;
    }

    retry(): void {
        if (!this.canRetry) return;

        // Drop the trailing error bubble before resending
        if (this.messages[this.messages.length - 1]?.error) {
            this.messages.pop();
        }
        this.request(this.lastQuestion!);
    }

    private request(question: string): void {
        this.sending = true;
        this.lastQuestion = question;
        this.scrollToBottom();

        // Prior turns only: exclude the just-asked question (last entry), skip
        // error bubbles, and cap at the most recent turns.
        const history = this.messages
            .slice(0, -1)
            .filter(m => !m.error)
            .slice(-MAX_HISTORY_TURNS)
            .map(m => ({ role: m.role, content: m.content }));

        this.assistantService.sendMessage(question, history).subscribe({
            next: res => {
                const answer = res.answer ?? '';
                this.messages.push({
                    role: 'assistant',
                    content: answer,
                    html: this.renderMarkdown(answer),
                    citations: res.citations ?? [],
                    toolCalls: res.toolCalls ?? [],
                });
                this.lastQuestion = null;
                this.sending = false;
                this.cdr.markForCheck();
                this.scrollToBottom();
            },
            error: err => {
                // ngxh6 wraps HTTP failures — the HttpErrorResponse sits at err.error
                const status = err?.error?.status ?? err?.status;
                if (status === 503) {
                    this.lastQuestion = null; // not configured — retrying won't help
                    this.messages.push({ role: 'assistant', content: "Assistant isn't configured yet.", error: true });
                } else if (status === 429) {
                    this.lastQuestion = null; // rate limited — no retry button
                    this.messages.push({ role: 'assistant', content: 'Slow down a little — try again in a minute.', error: true });
                } else {
                    this.messages.push({ role: 'assistant', content: 'Something went wrong. Please try again.', error: true });
                }
                this.sending = false;
                this.cdr.markForCheck();
                this.scrollToBottom();
            },
        });
    }

    // The assistant answers in GitHub-flavoured markdown (bold, tables, lists).
    // Rendered once here, not per change-detection cycle. Angular sanitises the
    // result on the [innerHTML] binding, so no bypassSecurityTrustHtml needed.
    private renderMarkdown(content: string): string {
        return marked.parse(content, { gfm: true, breaks: true, async: false }) as string;
    }

    private scrollToBottom(): void {
        requestAnimationFrame(() => {
            const el = this.thread?.nativeElement;
            if (el) el.scrollTop = el.scrollHeight;
        });
    }
}
